import { z } from 'zod';
import { CalendarDate } from './project.js';
import { GoalPeriodKind } from './performance-goal.js';
/**
 * Đánh giá một kỳ OKR đã kết thúc — APM-OKR5.
 *
 * ## ⚠️ Kết luận của **người**, đứng cạnh kết luận suy ra từ số
 *
 * `evaluateGoal()` đã trả lời "có chạm đích hay không" từ chính con số. Trường
 * `verdict` ở đây trả lời một câu khác: "kỳ này có đạt hay không". Hai câu ấy
 * **lệch nhau được**, và chỗ lệch là thứ đáng đọc nhất:
 *
 * - Chạm đích mà quản lý chấm `missed`: đích đặt quá dễ, hoặc con số bị lách.
 * - Trượt đích mà quản lý chấm `met`: hoàn cảnh đổi giữa kỳ, và người ấy đã làm
 *   đúng việc phải làm.
 *
 * Nên `verdict` **không** ghi đè `status` của `personal_goal`, và giao diện hiện
 * cả hai. Gộp chúng thành một con số là xoá mất cả hai tín hiệu — và xoá đúng
 * cái tín hiệu mà buổi đánh giá cuối kỳ sinh ra để tìm.
 *
 * ## `exceeded` tách khỏi `met`, và nó mang một con số
 *
 * "Đạt" và "vượt 40%" là hai kết cục khác nhau về mặt quản lý. Gộp chúng thì kỳ
 * sau không ai biết cái đích ấy đã đặt **quá thấp** — mà đó đúng là thứ Google
 * bảo phải nhìn: một bộ OKR đạt trọn vẹn là một bộ chưa nhắm đủ xa.
 */
export const OkrVerdict = z.enum(['met', 'missed', 'exceeded']);
/** Vượt bao nhiêu phần trăm so với đích — chỉ đi cùng `verdict = 'exceeded'`. */
const ExceededPct = z
    .number()
    .finite()
    .positive()
    .max(1_000)
    .describe('Vượt bao nhiêu phần trăm so với đích');
/**
 * Nhận xét gắn vào cái gì — APM-OKR6-c.
 *
 * Hai tầng, một bảng. Một `targetType` chứ không hai mảng riêng trong input:
 * `SaveOkrReviewCommand` xoá sạch rồi ghi lại, và hai đường ghi riêng nghĩa là
 * một lượt lưu chỉ mang nhận xét Objective sẽ **xoá mất** nhận xét Key Result
 * của chính lượt trước.
 */
export const OkrNoteTarget = z.enum(['objective', 'goal']);
/** Nhận xét cho một Objective hoặc một Key Result — tuỳ chọn, mọi trường đều có thể trống. */
export const OkrReviewNoteInput = z
    .object({
    targetType: OkrNoteTarget,
    targetId: z.uuid(),
    /**
     * ⚠️ `.trim()` đứng **trước** `.min()`.
     *
     * Zod áp `.trim()` như một phép biến đổi **sau** khi validate, nên
     * `.min(1).trim()` cho một chuỗi toàn dấu cách đi lọt rồi mới thành rỗng.
     */
    comment: z.string().trim().min(1).max(2_000).nullable().optional(),
    verdict: OkrVerdict.nullable().optional(),
    exceededPct: ExceededPct.nullable().optional(),
})
    .superRefine(requireExceededPct);
/**
 * `exceeded` phải đi kèm con số, và con số ấy **chỉ** có nghĩa với `exceeded`.
 *
 * Hai vế, và vế thứ hai quan trọng ngang vế thứ nhất: một `exceededPct` còn sót
 * lại khi người dùng đổi kết luận về "không đạt" sẽ nằm trong DB và hiện ra ở
 * một chỗ nào đó — "không đạt, vượt 30%" là một câu không ai giải thích được.
 */
function requireExceededPct(input, ctx) {
    if (input.verdict === 'exceeded' && (input.exceededPct === null || input.exceededPct === undefined)) {
        ctx.addIssue({
            code: 'custom',
            path: ['exceededPct'],
            message: 'Chọn "vượt mức" thì phải nhập vượt bao nhiêu phần trăm',
        });
    }
    if (input.verdict !== 'exceeded' && input.exceededPct !== null && input.exceededPct !== undefined) {
        ctx.addIssue({
            code: 'custom',
            path: ['exceededPct'],
            message: 'Chỉ kết luận "vượt mức" mới mang con số phần trăm',
        });
    }
}
/**
 * Lưu đánh giá một kỳ — một lời gọi cho **cả** nhận xét chung lẫn nhận xét từng mục.
 *
 * ## Vì sao một lời gọi chứ không hai
 *
 * Yêu cầu gốc: *"comment có comment trên từng OKR nếu cần, nhưng buộc phải
 * comment chung cho kỳ đó"*. Tách thành hai endpoint thì có một khoảng thời
 * gian mà nhận xét từng mục đã lưu còn nhận xét chung thì chưa — tức đúng
 * trạng thái mà chữ "buộc phải" ở trên tồn tại để ngăn.
 */
export const SaveOkrReviewInput = z
    .object({
    periodKind: GoalPeriodKind,
    periodStart: CalendarDate,
    periodEnd: CalendarDate,
    /**
     * Nhận xét chung cho cả kỳ. **Lưu được khi bỏ trống** (người dùng chốt
     * 2026-09-22) — nhưng khi ấy kỳ **chưa được tính là đã đánh giá**.
     *
     * Xem `okrPeriodReviewed`: chỉ nhận xét chung mới làm một kỳ thành "đã
     * đánh giá". Nhận xét riêng cho từng mục tiêu, dù có bao nhiêu, cũng không.
     */
    comment: z.string().trim().min(1).max(4_000).nullable().optional(),
    verdict: OkrVerdict.nullable().optional(),
    exceededPct: ExceededPct.nullable().optional(),
    /**
     * Nhận xét từng mục — Objective lẫn Key Result. Mục nào không nhắc tới thì
     * nhận xét cũ bị xoá.
     *
     * Trần 40 chứ không 20 từ APM-OKR6-c: một kỳ giờ có tới ba Objective và
     * bốn KR mỗi cái, và trần cũ sẽ chặn đúng một kỳ đầy đủ.
     */
    notes: z.array(OkrReviewNoteInput).max(40).optional(),
})
    .superRefine(requireExceededPct)
    .superRefine((input, ctx) => {
    /*
      Nhận xét chung và kết luận đi **thành cặp**.

      Một kết luận "không đạt" không kèm câu nào giải thích là thứ tệ nhất màn
      hình này phát ra được; một câu nhận xét không kèm kết luận thì cuối kỳ
      không ai đọc ra được nó nói đạt hay không.
    */
    if ((input.comment ?? null) === null && (input.verdict ?? null) !== null) {
        ctx.addIssue({
            code: 'custom',
            path: ['comment'],
            message: 'Có kết luận thì phải có nhận xét chung đi kèm',
        });
    }
    if ((input.comment ?? null) !== null && (input.verdict ?? null) === null) {
        ctx.addIssue({
            code: 'custom',
            path: ['verdict'],
            message: 'Có nhận xét chung thì phải chốt đạt hay không đạt',
        });
    }
    /*
      Lưu một bản ghi **rỗng hoàn toàn** là ghi một hàng không mang thông tin
      nào — và nó vẫn hiện ra như một lượt đánh giá đã có.
    */
    const notes = (input.notes ?? []).filter((note) => note.comment ?? note.verdict ?? null);
    if ((input.comment ?? null) === null && notes.length === 0) {
        ctx.addIssue({
            code: 'custom',
            path: ['comment'],
            message: 'Phải có ít nhất một nhận xét — chung cho kỳ, hoặc cho một mục tiêu',
        });
    }
});
export const OkrReviewNoteView = z.object({
    targetType: OkrNoteTarget,
    targetId: z.uuid(),
    comment: z.string().nullable(),
    verdict: OkrVerdict.nullable(),
    exceededPct: z.number().nullable(),
});
export const OkrReviewView = z.object({
    id: z.uuid(),
    ownerId: z.uuid(),
    periodKind: GoalPeriodKind,
    periodStart: CalendarDate,
    periodEnd: CalendarDate,
    /** `null` = kỳ này mới chỉ có nhận xét cho từng mục, chưa có nhận xét chung. */
    comment: z.string().nullable(),
    verdict: OkrVerdict.nullable(),
    exceededPct: z.number().nullable(),
    reviewedBy: z.uuid(),
    /** Tên người chốt — người bị đánh giá có quyền biết ai đã chốt. */
    reviewerName: z.string(),
    notes: z.array(OkrReviewNoteView),
    createdAt: z.iso.datetime(),
    updatedAt: z.iso.datetime(),
});
/**
 * Kỳ đã kết thúc chưa — cửa duy nhất quyết định có đánh giá được hay không.
 *
 * Hàm thuần dùng chung FE↔BE: giao diện ẩn form và lệnh từ chối phải đi theo
 * **cùng một** phép so, nếu không thì hoặc nút hiện ra để nhận lỗi, hoặc nút
 * biến mất ở một kỳ vẫn đánh giá được.
 *
 * So bằng chuỗi `YYYY-MM-DD`: `periodEnd` là ngày lịch, không phải mốc giờ, nên
 * đổi nó sang `Date` chỉ để so là mời một lệch múi giờ vào đúng chỗ quyết định
 * "kỳ đã hết chưa" — cùng lý lẽ với `evaluateGoal`.
 */
export function okrPeriodReviewable(periodEnd, at) {
    return periodEnd < at.toISOString().slice(0, 10);
}
/**
 * Kỳ này đã được **đánh giá** chưa — người dùng chốt 2026-09-22.
 *
 * ⚠️ Chỉ **nhận xét chung** mới tính. Nhận xét riêng cho từng mục tiêu, dù có
 * bao nhiêu, cũng không làm một kỳ thành "đã đánh giá".
 *
 * Lý do: nhận xét từng mục trả lời "mục này thế nào", còn kỳ thì cần một câu
 * trả lời cho "kỳ này thế nào" — và ba nhận xét mục rời rạc không tự cộng lại
 * thành câu ấy. Một kỳ có ba ghi chú mục mà không có dòng nào nói về cả kỳ vẫn
 * là một kỳ chưa ai ngồi xuống tổng kết.
 *
 * Hàm thuần dùng chung FE↔BE: giao diện và mọi phép đếm phải hỏi **cùng một**
 * câu, nếu không thì bảng "ai chưa đánh giá" đếm khác màn hình.
 */
export function okrPeriodReviewed(review) {
    return (review?.comment ?? null) !== null;
}
/**
 * Một người trong ô chọn, kèm **kỳ trước của họ đã được đánh giá chưa** —
 * APM-OKR5, người dùng yêu cầu 2026-09-22.
 *
 * ## Vì sao trạng thái đi cùng danh sách người, không phải một lời gọi mỗi người
 *
 * Ô chọn có tới 200 dòng (trần của `goal-subjects`). Hỏi từng người một là 200
 * lượt gọi để vẽ **một** ô chọn — và chúng về không theo thứ tự, nên nhãn nhấp
 * nháy khi từng cái tới.
 *
 * ## `reviewed` chứ không `hasReview`
 *
 * Một bản ghi có thể tồn tại mà kỳ vẫn **chưa được đánh giá**: nó mới chỉ mang
 * nhận xét cho từng mục tiêu. Xem `okrPeriodReviewed`.
 */
export const OkrReviewStatus = z.object({
    userId: z.uuid(),
    userName: z.string(),
    /** Kỳ đang được hỏi — kỳ **liền trước** kỳ đang chạy, theo `periodKind`. */
    periodStart: CalendarDate,
    periodEnd: CalendarDate,
    reviewed: z.boolean(),
    /** Người này có mục tiêu nào trong kỳ ấy không — "chưa đánh giá" của một kỳ
     *  trống nghĩa khác hẳn "chưa đánh giá" của một kỳ có ba mục tiêu. */
    goalCount: z.number().int().nonnegative(),
    /**
     * OKR **tháng tới** đang soạn — người dùng yêu cầu 2026-09-30: quản lý trực
     * tiếp phải thấy nhân sự nào đã bắt đầu soạn, nhân sự nào chưa, mà không bấm
     * vào từng người. Luôn là tháng kế tiếp (tính ở server, theo ngày của tổ
     * chức), bất kể `periodKind` đang hỏi — tháng là nhịp soạn dày nhất.
     */
    nextMonth: z.object({
        periodStart: CalendarDate,
        objectiveCount: z.number().int().nonnegative(),
        keyResultCount: z.number().int().nonnegative(),
    }),
});
//# sourceMappingURL=okr-review.js.map